/**
 * Per-browser cache of IFC bytes, keyed by SHA-256.
 *
 * Onda 6c: promotes the file picker / handle dance into a true
 * "open session = files reload" experience:
 *
 *  1. Every IFC the user loads has its bytes saved here.
 *  2. Opening a `.vn2r-session` checks each expected SHA-256 against
 *     this store; matches load instantly with zero user interaction
 *     (no permission prompt, no file picker).
 *  3. Misses fall through to the existing FileSystemFileHandle path
 *     and finally to a manual file picker.
 *
 * Trade-offs:
 *
 *  - Disk: a 700 MB IFC takes 700 MB in IDB. Browsers normally allow
 *    50%+ of free disk per origin, so this fits comfortably for a
 *    handful of models. We surface a soft cap (DEFAULT_MAX_BYTES)
 *    and skip storage for any IFC bigger than the cap so a single
 *    monstrous file does not lock the user out of the cache.
 *  - Eviction: the browser may evict IDB under storage pressure if
 *    the origin has not requested persistent storage. The user-
 *    facing storage warning (Diagnostics) prompts the user to
 *    request persistent storage when usage gets close to quota.
 *  - Multi-user: each browser profile has its own IDB origin, so
 *    two users on the same machine never see each other's cache.
 */

import { markWorkDirty } from '@core/session/recovery'

const DB_NAME = 'vn2r.ifcs'
const DB_VERSION = 1
const STORE = 'bytes'

/** Skip caching if the file is bigger than this. 700 MB fits in
 * Chrome's default per-origin quota easily, but the user can still
 * load (just won't auto-restore). */
export const DEFAULT_MAX_BYTES = 700 * 1024 * 1024

export interface IfcBlobRecord {
  sha256: string
  filename: string
  sizeBytes: number
  blob: Blob
  importedAt: string
  lastUsedAt: string
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'sha256' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => {
      dbPromise = null
      reject(req.error ?? new Error('IDB open failed'))
    }
  })
  return dbPromise
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const db = await openDb()
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode)
    const store = tx.objectStore(STORE)
    let result: T
    Promise.resolve(fn(store))
      .then((r) => {
        result = r as T
      })
      .catch(reject)
    tx.oncomplete = () => resolve(result)
    tx.onerror = () =>
      reject(tx.error ?? new Error('IDB transaction failed'))
    tx.onabort = () => reject(tx.error ?? new Error('IDB transaction aborted'))
  })
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('IDB request failed'))
  })
}

export const ifcPersistence = {
  /** Save IFC bytes. Returns false if the file exceeded the size
   * cap and was skipped. */
  async save(opts: {
    sha256: string
    filename: string
    bytes: ArrayBuffer | Uint8Array
    maxBytes?: number
  }): Promise<boolean> {
    const cap = opts.maxBytes ?? DEFAULT_MAX_BYTES
    const buf =
      opts.bytes instanceof Uint8Array
        ? opts.bytes.slice().buffer
        : opts.bytes.slice(0)
    const size = (buf as ArrayBuffer).byteLength
    if (size > cap) {
      console.info(
        `[ifcPersistence] skipping cache for ${opts.filename}: ${size} > cap ${cap}`,
      )
      return false
    }
    const blob = new Blob([buf], { type: 'application/octet-stream' })
    const now = new Date().toISOString()
    const record: IfcBlobRecord = {
      sha256: opts.sha256.toLowerCase(),
      filename: opts.filename,
      sizeBytes: size,
      blob,
      importedAt: now,
      lastUsedAt: now,
    }
    try {
      // `isNew` distingue "o utilizador abriu um IFC" (trabalho novo,
      // sessão passa a estar por gravar) de "estamos a reabrir bytes
      // que já cá estavam" (recuperação ou abertura de um
      // .vn2r-session): reescrever a mesma cache não suja nada.
      const isNew = await withStore('readwrite', async (s) => {
        const existing = await reqToPromise(s.getKey(record.sha256))
        await reqToPromise(s.put(record))
        return existing === undefined
      })
      if (isNew) markWorkDirty({ ifcName: opts.filename })
      return true
    } catch (err) {
      console.warn('[ifcPersistence] save failed', err)
      return false
    }
  },

  async load(sha256: string): Promise<IfcBlobRecord | null> {
    try {
      const rec = await withStore<IfcBlobRecord | undefined>(
        'readonly',
        (s) => reqToPromise(s.get(sha256.toLowerCase())) as Promise<
          IfcBlobRecord | undefined
        >,
      )
      return rec ?? null
    } catch {
      return null
    }
  },

  async touch(sha256: string): Promise<void> {
    try {
      const rec = await ifcPersistence.load(sha256)
      if (!rec) return
      rec.lastUsedAt = new Date().toISOString()
      await withStore('readwrite', (s) => reqToPromise(s.put(rec)))
    } catch {
      /* ignore */
    }
  },

  async remove(sha256: string): Promise<void> {
    try {
      await withStore('readwrite', (s) =>
        reqToPromise(s.delete(sha256.toLowerCase())),
      )
    } catch {
      /* ignore */
    }
  },

  async list(): Promise<IfcBlobRecord[]> {
    try {
      return await withStore<IfcBlobRecord[]>('readonly', (s) =>
        reqToPromise(s.getAll() as IDBRequest<IfcBlobRecord[]>),
      )
    } catch {
      return []
    }
  },

  async clearAll(): Promise<void> {
    try {
      await withStore('readwrite', (s) => reqToPromise(s.clear()))
    } catch {
      /* ignore */
    }
  },

  /** Convenience: turn a stored record into a File the loader accepts. */
  toFile(rec: IfcBlobRecord): File {
    return new File([rec.blob], rec.filename, {
      type: 'application/octet-stream',
      lastModified: new Date(rec.importedAt).getTime(),
    })
  },
}
